import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  Menu,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconDotsVertical, IconPencil, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { describeRpcError, usePermission } from "@how/app-kit";
import { ConfirmActionModal, ErrorState, LoadingState, RecordDrawer } from "@how/ui";
import { RefundModal } from "./RefundModal";
import { usePeopleOptions } from "../people/household-queries";
import { EventModal } from "./EventModal";
import {
  type EventRow,
  formatMoney,
  formatWhen,
  type Occurrence,
  type Registration,
  REGISTRATION_LABEL,
  type RunFields,
  useAddOccurrences,
  useAttachGuest,
  useEvents,
  useOccurrences,
  useRecordAttendance,
  useRegisterPerson,
  useRegistrations,
  useSetRegistrationStatus,
  useUpdateOccurrence,
  VISIBILITY_LABEL,
} from "./event-queries";
import { EventAddOnsSection } from "./EventAddOnsSection";
import { RosterAddOns } from "./RosterAddOns";

export interface EventDrawerProps {
  readonly eventId: string | null;
  readonly onClose: () => void;
}

const EMPTY_RUN: RunFields = { startsAt: "", minutes: 90, everyDays: null, until: "", note: "" };

/**
 * One event: what it is, its dates, and for the date on screen who is registered, who is waiting, who came, and
 * the guests from the website. Dates take events.manage; the roster takes registrations.manage; the database
 * decides. A child's registration row is absent for a caller without people.read_minors (ADR 0028), and the
 * date's count still includes them.
 */
export function EventDrawer({ eventId, onClose }: EventDrawerProps) {
  const events = useEvents({ search: "", status: "all", when: "all" });
  const event: EventRow | undefined = events.data?.find((e) => e.id === eventId);
  const occurrences = useOccurrences(eventId);
  const [occurrenceId, setOccurrenceId] = useState<string | null>(null);
  const canManage = usePermission("events.manage", { write: true });
  const canRegister = usePermission("registrations.manage", { write: true });
  const canPickPeople = usePermission("people.read");
  const canRefund = usePermission("commerce.refund_orders", { module: "events", write: true });
  const [refunding, setRefunding] = useState<Registration | null>(null);
  const manage = canManage.data === true;
  const register = canRegister.data === true;

  const upcoming = (occurrences.data ?? []).filter(
    (o) => !o.isCancelled && new Date(o.startsAt).getTime() >= Date.now() - 3600_000,
  );
  const current: Occurrence | undefined =
    (occurrences.data ?? []).find((o) => o.id === occurrenceId) ??
    upcoming[0] ??
    occurrences.data?.[0];
  const registrations = useRegistrations(current?.id ?? null);

  const addRun = useAddOccurrences();
  const updateOccurrence = useUpdateOccurrence();
  const registerPerson = useRegisterPerson();
  const setStatus = useSetRegistrationStatus();
  const attach = useAttachGuest();
  const attendance = useRecordAttendance();

  const [editing, setEditing] = useState(false);
  const [addingRun, setAddingRun] = useState(false);
  const [run, setRun] = useState<RunFields>(EMPTY_RUN);
  const [cancelling, setCancelling] = useState<Occurrence | null>(null);
  const [search, setSearch] = useState("");
  const [newPerson, setNewPerson] = useState<string | null>(null);
  const [attaching, setAttaching] = useState<Registration | null>(null);
  const [attachTo, setAttachTo] = useState<string | null>(null);
  const [attachSearch, setAttachSearch] = useState("");

  const rows = registrations.data ?? [];
  const options = usePeopleOptions(
    search,
    rows
      .filter((r) => r.personId !== null && r.status !== "cancelled")
      .map((r) => r.personId ?? ""),
  );
  const attachOptions = usePeopleOptions(attachSearch, []);
  const registered = rows.filter((r) => r.status === "registered");
  const waiting = rows.filter((r) => r.status === "waitlisted");
  const attended = rows.filter((r) => r.status === "attended");
  const cancelled = rows.filter((r) => r.status === "cancelled");

  const close = () => {
    setEditing(false);
    setAddingRun(false);
    setOccurrenceId(null);
    setSearch("");
    setNewPerson(null);
    onClose();
  };

  const runValid =
    run.startsAt !== "" &&
    run.minutes >= 1 &&
    (run.everyDays === null || (run.everyDays >= 1 && run.until !== ""));

  return (
    <RecordDrawer
      opened={eventId !== null}
      onClose={close}
      title={event?.title ?? "Event"}
      subtitle={
        event === undefined ? undefined : (
          <Group gap="xs">
            <Badge variant="light" color={event.isActive ? "teal" : "gray"}>
              {event.isActive ? "Active" : "Archived"}
            </Badge>
            <Text size="sm" c="dimmed">
              {[event.categoryName, event.campusName].filter((p) => p !== null).join(" · ")}
            </Text>
          </Group>
        )
      }
    >
      {event === undefined ? (
        events.isPending ? (
          <LoadingState rows={4} />
        ) : (
          <ErrorState message="This event is not one you can see." />
        )
      ) : (
        <Stack gap="lg" mt="md">
          <section>
            <Group justify="space-between" align="center">
              <Title order={4}>About</Title>
              {manage ? (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPencil size={14} />}
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </Group>
            {event.description === null ? (
              <Text size="sm" c="dimmed" mt="xs">
                No description yet. Members read it before they register.
              </Text>
            ) : (
              <Text size="sm" mt="xs" style={{ whiteSpace: "pre-wrap" }}>
                {event.description}
              </Text>
            )}
            <SimpleGrid cols={2} spacing="xs" mt="sm">
              <Fact label="Where" value={event.location} />
              <Fact label="Ministry" value={event.ministry} />
              <Fact label="Who can see it" value={VISIBILITY_LABEL[event.visibility]} />
              <Fact label="Registration" value={REGISTRATION_LABEL[event.registration]} />
              <Fact
                label="Capacity per date"
                value={event.capacity === null ? "No limit" : String(event.capacity)}
              />
              <Fact label="Campus" value={event.campusName} />
            </SimpleGrid>
          </section>

          <EventAddOnsSection eventId={event.id} />

          <section>
            <Group justify="space-between" align="center">
              <Title order={4}>Dates</Title>
              {manage ? (
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => {
                    setRun(EMPTY_RUN);
                    setAddingRun(true);
                  }}
                >
                  Add dates
                </Button>
              ) : null}
            </Group>
            {occurrences.isPending ? (
              <LoadingState rows={2} />
            ) : (occurrences.data ?? []).length === 0 ? (
              <Text size="sm" c="dimmed" mt="xs">
                No dates yet. An event without a date is not on anyone's calendar.
              </Text>
            ) : (
              <Select
                aria-label="Date"
                mt="xs"
                data={(occurrences.data ?? []).map((o) => ({
                  value: o.id,
                  label: `${formatWhen(o.startsAt, event.campusTimeZone)}${o.isCancelled ? " (cancelled)" : ""} · ${String(o.registered + o.attended)} registered${o.waitlisted > 0 ? `, ${String(o.waitlisted)} waiting` : ""}`,
                }))}
                value={current?.id ?? null}
                allowDeselect={false}
                onChange={(value) => {
                  setOccurrenceId(value);
                }}
              />
            )}
            {current !== undefined && current.note !== null ? (
              <Text size="xs" c="dimmed" mt={4}>
                {current.note}
              </Text>
            ) : null}
            {current !== undefined && manage ? (
              <Group gap="xs" mt="xs">
                {current.isCancelled ? (
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    loading={updateOccurrence.isPending}
                    onClick={() => {
                      updateOccurrence.mutate({
                        eventId: event.id,
                        occurrenceId: current.id,
                        startsAt: current.startsAt,
                        endsAt: current.endsAt,
                        note: current.note ?? "",
                        isCancelled: false,
                      });
                    }}
                  >
                    Restore this date
                  </Button>
                ) : (
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      setCancelling(current);
                    }}
                  >
                    Cancel this date
                  </Button>
                )}
              </Group>
            ) : null}
            {addingRun ? (
              <Stack gap="xs" mt="sm">
                <Group grow align="flex-start">
                  <TextInput
                    label="First date and time"
                    type="datetime-local"
                    value={run.startsAt}
                    onChange={(e) => {
                      const startsAt = e.currentTarget.value;
                      setRun((f) => ({ ...f, startsAt }));
                    }}
                  />
                  <NumberInput
                    label="Lasts (minutes)"
                    min={1}
                    max={10080}
                    value={run.minutes}
                    onChange={(value) => {
                      setRun((f) => ({ ...f, minutes: typeof value === "number" ? value : 90 }));
                    }}
                  />
                </Group>
                <Group grow align="flex-start">
                  <Select
                    label="Repeats"
                    data={[
                      { value: "0", label: "Once" },
                      { value: "7", label: "Every week" },
                      { value: "14", label: "Every two weeks" },
                      { value: "1", label: "Every day" },
                    ]}
                    value={run.everyDays === null ? "0" : String(run.everyDays)}
                    allowDeselect={false}
                    onChange={(value) => {
                      const n = Number(value ?? "0");
                      setRun((f) => ({ ...f, everyDays: n === 0 ? null : n }));
                    }}
                  />
                  <TextInput
                    label="Until"
                    type="date"
                    description="At most a year ahead; add more later."
                    disabled={run.everyDays === null}
                    value={run.until}
                    onChange={(e) => {
                      const until = e.currentTarget.value;
                      setRun((f) => ({ ...f, until }));
                    }}
                  />
                </Group>
                <TextInput
                  label="Note on these dates"
                  placeholder="Bring a coat"
                  value={run.note}
                  onChange={(e) => {
                    const note = e.currentTarget.value;
                    setRun((f) => ({ ...f, note }));
                  }}
                />
                {addRun.isError ? (
                  <Text size="sm" c="red">
                    {describeRpcError(addRun.error).message}
                  </Text>
                ) : null}
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => {
                      setAddingRun(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!runValid}
                    loading={addRun.isPending}
                    onClick={() => {
                      addRun.mutate(
                        { eventId: event.id, fields: run },
                        {
                          onSuccess: () => {
                            setAddingRun(false);
                          },
                        },
                      );
                    }}
                  >
                    Add dates
                  </Button>
                </Group>
              </Stack>
            ) : null}
          </section>

          {current === undefined ? null : (
            <section>
              <Title order={4}>Who is coming</Title>
              {registrations.isPending ? (
                <LoadingState rows={3} />
              ) : registrations.isError ? (
                <ErrorState message="Could not load the registrations." />
              ) : (
                <Stack gap="md" mt="xs">
                  {rows.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      Nobody registered for this date
                      {event.registration === "none" ? "; this event takes no registrations." : "."}
                    </Text>
                  ) : null}
                  {[
                    ["Registered", registered],
                    ["Waiting list", waiting],
                    ["Came", attended],
                  ].map(([label, list]) =>
                    (list as Registration[]).length === 0 ? null : (
                      <Stack key={label as string} gap="xs">
                        <Text size="sm" fw={600}>
                          {label as string}
                        </Text>
                        {(list as Registration[]).map((r) => (
                          <Group key={r.id} justify="space-between" wrap="nowrap">
                            <div>
                              <Group gap="xs" wrap="nowrap">
                                <Text size="sm">{r.name}</Text>
                                {r.isGuest ? (
                                  <Badge size="sm" variant="light" color="yellow">
                                    Guest
                                  </Badge>
                                ) : null}
                                {r.paymentStatus === "paid" ? (
                                  <Badge size="sm" variant="light" color="teal">
                                    {r.refundedCents > 0
                                      ? `Paid, ${formatMoney(r.refundedCents, r.currency)} refunded`
                                      : "Paid"}
                                  </Badge>
                                ) : r.paymentStatus === "refunded" ? (
                                  <Badge size="sm" variant="light" color="gray">
                                    Refunded
                                  </Badge>
                                ) : r.paymentStatus === "pending" ? (
                                  <Badge size="sm" variant="light" color="orange">
                                    Unpaid
                                  </Badge>
                                ) : null}
                              </Group>
                              <RosterAddOns registration={r} />
                              <Text size="xs" c="dimmed">
                                {r.isGuest ? r.guestEmail : null}
                                {r.registeredByName === null
                                  ? null
                                  : `Registered by ${r.registeredByName}`}
                                {r.note === null ? null : ` · “${r.note}”`}
                              </Text>
                            </div>
                            {register ? (
                              <Menu position="bottom-end" withinPortal>
                                <Menu.Target>
                                  <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    aria-label={`Actions for ${r.name}`}
                                  >
                                    <IconDotsVertical size={16} />
                                  </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  {r.status === "waitlisted" ? (
                                    <Menu.Item
                                      onClick={() => {
                                        setStatus.mutate({
                                          eventId: event.id,
                                          occurrenceId: current.id,
                                          registrationId: r.id,
                                          status: "registered",
                                        });
                                      }}
                                    >
                                      Move up from the waiting list
                                    </Menu.Item>
                                  ) : null}
                                  {r.isGuest ? (
                                    <Menu.Item
                                      onClick={() => {
                                        setAttaching(r);
                                      }}
                                    >
                                      Attach to a person
                                    </Menu.Item>
                                  ) : r.status === "attended" ? (
                                    <Menu.Item
                                      onClick={() => {
                                        attendance.mutate({
                                          eventId: event.id,
                                          occurrenceId: current.id,
                                          personId: r.personId ?? "",
                                          present: false,
                                        });
                                      }}
                                    >
                                      Did not come after all
                                    </Menu.Item>
                                  ) : (
                                    <Menu.Item
                                      onClick={() => {
                                        attendance.mutate({
                                          eventId: event.id,
                                          occurrenceId: current.id,
                                          personId: r.personId ?? "",
                                          present: true,
                                        });
                                      }}
                                    >
                                      Mark as came
                                    </Menu.Item>
                                  )}
                                  {r.paymentStatus === "paid" && canRefund.data === true ? (
                                    <Menu.Item
                                      onClick={() => {
                                        setRefunding(r);
                                      }}
                                    >
                                      Refund…
                                    </Menu.Item>
                                  ) : null}
                                  {r.status !== "attended" ? (
                                    <Menu.Item
                                      color="red"
                                      onClick={() => {
                                        setStatus.mutate({
                                          eventId: event.id,
                                          occurrenceId: current.id,
                                          registrationId: r.id,
                                          status: "cancelled",
                                        });
                                      }}
                                    >
                                      Cancel registration
                                    </Menu.Item>
                                  ) : null}
                                </Menu.Dropdown>
                              </Menu>
                            ) : null}
                          </Group>
                        ))}
                      </Stack>
                    ),
                  )}
                  {cancelled.length > 0 ? (
                    <Text size="xs" c="dimmed">
                      Cancelled: {cancelled.map((r) => r.name).join(", ")}
                    </Text>
                  ) : null}
                  {current.registered + current.attended + current.waitlisted >
                  registered.length + attended.length + waiting.length ? (
                    <Text size="xs" c="dimmed">
                      {String(
                        current.registered +
                          current.attended +
                          current.waitlisted -
                          registered.length -
                          attended.length -
                          waiting.length,
                      )}{" "}
                      more on this date are children not shown; seeing children&apos;s records takes
                      a permission the church grants on purpose.
                    </Text>
                  ) : null}
                  {register && canPickPeople.data === true && !current.isCancelled ? (
                    <Stack gap="xs">
                      <Divider />
                      <Text size="sm" fw={600}>
                        Register someone
                      </Text>
                      <Group gap="xs" align="flex-end" wrap="nowrap">
                        <Select
                          aria-label="Person"
                          placeholder="Search by name"
                          searchable
                          style={{ flex: 1 }}
                          searchValue={search}
                          onSearchChange={setSearch}
                          data={options.data ?? []}
                          value={newPerson}
                          onChange={setNewPerson}
                          nothingFoundMessage={options.isPending ? "Searching" : "Nobody matches"}
                          filter={({ options: all }) => all}
                        />
                        <Button
                          size="sm"
                          disabled={newPerson === null}
                          loading={registerPerson.isPending}
                          onClick={() => {
                            if (newPerson === null) return;
                            registerPerson.mutate(
                              { eventId: event.id, occurrenceId: current.id, personId: newPerson },
                              {
                                onSuccess: () => {
                                  setNewPerson(null);
                                  setSearch("");
                                },
                              },
                            );
                          }}
                        >
                          Register
                        </Button>
                      </Group>
                      {registerPerson.isError ? (
                        <Text size="sm" c="red">
                          {describeRpcError(registerPerson.error).message}
                        </Text>
                      ) : null}
                    </Stack>
                  ) : null}
                </Stack>
              )}
            </section>
          )}
        </Stack>
      )}

      <EventModal
        key={editing ? `edit-${event?.id ?? ""}` : "closed"}
        event={editing ? event : undefined}
        canManage={manage}
        onClose={() => {
          setEditing(false);
        }}
      />

      <ConfirmActionModal
        opened={cancelling !== null}
        onClose={() => {
          setCancelling(null);
        }}
        title="Cancel this date"
        message={
          cancelling === null || event === undefined
            ? ""
            : `Cancel ${event.title} on ${formatWhen(cancelling.startsAt, event.campusTimeZone)}? The date stays, marked cancelled, so the ${String(cancelling.registered + cancelling.waitlisted)} registered can be told. The other dates are untouched.`
        }
        confirmLabel="Cancel this date"
        danger
        loading={updateOccurrence.isPending}
        onConfirm={() => {
          if (cancelling === null || event === undefined) return;
          updateOccurrence.mutate(
            {
              eventId: event.id,
              occurrenceId: cancelling.id,
              startsAt: cancelling.startsAt,
              endsAt: cancelling.endsAt,
              note: cancelling.note ?? "",
              isCancelled: true,
            },
            {
              onSuccess: () => {
                setCancelling(null);
              },
            },
          );
        }}
      />

      <Modal
        opened={attaching !== null}
        onClose={() => {
          setAttaching(null);
          setAttachTo(null);
          setAttachSearch("");
        }}
        title="Attach this guest to a person"
        radius="lg"
      >
        <Stack gap="sm">
          <Text size="sm">
            {attaching === null
              ? ""
              : `${attaching.name} (${attaching.guestEmail ?? ""}) registered from the website. Attaching makes this registration that person's; the guest name and email go. Nothing is created: choose a person the church already knows, or add them under People first.`}
          </Text>
          <Select
            aria-label="Person"
            placeholder="Search by name"
            searchable
            searchValue={attachSearch}
            onSearchChange={setAttachSearch}
            data={attachOptions.data ?? []}
            value={attachTo}
            onChange={setAttachTo}
            nothingFoundMessage={attachOptions.isPending ? "Searching" : "Nobody matches"}
            filter={({ options: all }) => all}
          />
          {attach.isError ? (
            <Text size="sm" c="red">
              {describeRpcError(attach.error).message}
            </Text>
          ) : null}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => {
                setAttaching(null);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={attachTo === null}
              loading={attach.isPending}
              onClick={() => {
                if (
                  attaching === null ||
                  attachTo === null ||
                  event === undefined ||
                  current === undefined
                )
                  return;
                attach.mutate(
                  {
                    eventId: event.id,
                    occurrenceId: current.id,
                    registrationId: attaching.id,
                    personId: attachTo,
                  },
                  {
                    onSuccess: () => {
                      setAttaching(null);
                      setAttachTo(null);
                      setAttachSearch("");
                    },
                  },
                );
              }}
            >
              Attach
            </Button>
          </Group>
        </Stack>
      </Modal>
      {refunding === null ? null : (
        <RefundModal
          registration={refunding}
          onClose={() => {
            setRefunding(null);
          }}
        />
      )}
    </RecordDrawer>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {value === null ? (
        <Text size="sm" c="dimmed">
          Not set
        </Text>
      ) : (
        <Text size="sm">{value}</Text>
      )}
    </div>
  );
}
